import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API } from '../config/api';
import {
  Music, Disc3, Mic, Video, Palette, Settings, Building2, Radio, Headphones,
  MapPin, Play, Pause, Heart, Users, Share2, UserPlus, UserMinus,
  MessageSquare, ChevronLeft, Loader2, X, Briefcase, DollarSign,
  Clock, Tag, ExternalLink, Calendar, Star, Image as ImageIcon
} from 'lucide-react';

const ROLE_ICONS = { artist: Music, producer: Disc3, podcaster: Mic, videographer: Video, designer: Palette, engineer: Settings, studio: Building2, streamer: Radio, listener: Headphones };
const ROLE_COLORS = {
  artist: { badge: 'bg-violet-500/20 text-violet-400 border-violet-500/30', accent: 'bg-violet-600', gradient: 'from-violet-900/40 to-zinc-900' },
  producer: { badge: 'bg-orange-500/20 text-orange-400 border-orange-500/30', accent: 'bg-orange-600', gradient: 'from-orange-900/40 to-zinc-900' },
  podcaster: { badge: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', accent: 'bg-emerald-600', gradient: 'from-emerald-900/40 to-zinc-900' },
  videographer: { badge: 'bg-sky-500/20 text-sky-400 border-sky-500/30', accent: 'bg-sky-600', gradient: 'from-sky-900/40 to-zinc-900' },
  designer: { badge: 'bg-pink-500/20 text-pink-400 border-pink-500/30', accent: 'bg-pink-600', gradient: 'from-pink-900/40 to-zinc-900' },
  engineer: { badge: 'bg-amber-500/20 text-amber-400 border-amber-500/30', accent: 'bg-amber-600', gradient: 'from-amber-900/40 to-zinc-900' },
  studio: { badge: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30', accent: 'bg-indigo-600', gradient: 'from-indigo-900/40 to-zinc-900' },
  streamer: { badge: 'bg-red-500/20 text-red-400 border-red-500/30', accent: 'bg-red-600', gradient: 'from-red-900/40 to-zinc-900' },
};
const CONTENT_TITLES = { tracks: 'Tracks', beats: 'Beat Catalog', episodes: 'Episodes', portfolio: 'Portfolio', services: 'Services', streams: 'Stream History' };

// ── Content renderers per type ──────────────────────────────────────

function BeatItem({ item, onPlay, isPlaying }) {
  return (
    <div className="group rounded-xl overflow-hidden bg-muted/20 border border-border hover:border-orange-500/30 transition-all" data-testid={`beat-${item.beat_id}`}>
      <div className="aspect-square relative cursor-pointer" onClick={() => onPlay(item)}>
        {item.cover_art_url ? <img src={item.cover_art_url} alt={item.title} className="w-full h-full object-cover" />
          : <div className="w-full h-full bg-gradient-to-br from-orange-600/20 to-amber-600/10 flex items-center justify-center"><Disc3 className="w-10 h-10 text-orange-400/50" /></div>}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
          <div className={`w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center transform ${isPlaying ? 'scale-100' : 'scale-0 group-hover:scale-100'} transition-transform`}>
            {isPlaying ? <Pause className="w-4 h-4 text-white" /> : <Play className="w-4 h-4 text-white ml-0.5" />}
          </div>
        </div>
        {item.price && !item.is_free && <div className="absolute top-2 right-2 bg-background/70 text-xs px-2 py-0.5 rounded-full text-orange-300">${item.price}</div>}
        {item.is_free && <div className="absolute top-2 right-2 bg-green-600/80 text-xs px-2 py-0.5 rounded-full text-white">FREE</div>}
      </div>
      <div className="p-3">
        <h4 className="font-medium text-sm truncate">{item.title}</h4>
        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
          {item.genre && <span>{item.genre}</span>}
          {item.bpm && <span>{item.bpm} BPM</span>}
        </div>
      </div>
    </div>
  );
}

function EpisodeItem({ item, onPlay, isPlaying }) {
  return (
    <div className="flex gap-3 p-3 rounded-xl bg-muted/20 border border-border hover:border-emerald-500/20 transition-all" data-testid={`episode-${item.episode_id}`}>
      <div className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden cursor-pointer" onClick={() => onPlay(item)}>
        {item.cover_art_url ? <img src={item.cover_art_url} alt="" className="w-full h-full object-cover" />
          : <div className="w-full h-full bg-emerald-900/30 flex items-center justify-center"><Mic className="w-6 h-6 text-emerald-400/50" /></div>}
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-sm truncate">{item.title}</h4>
        {item.description && <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{item.description}</p>}
        <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
          {item.season && <span>S{item.season} E{item.episode_number}</span>}
          {item.duration && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{item.duration}</span>}
          <span>{item.plays_count || 0} plays</span>
        </div>
      </div>
      <button onClick={() => onPlay(item)} className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center self-center">
        {isPlaying ? <Pause className="w-4 h-4 text-white" /> : <Play className="w-4 h-4 text-white ml-0.5" />}
      </button>
    </div>
  );
}

function PortfolioItem({ item }) {
  return (
    <div className="group rounded-xl overflow-hidden bg-muted/20 border border-border hover:border-pink-500/20 transition-all" data-testid={`portfolio-${item.item_id}`}>
      <div className="aspect-[4/3] relative">
        {item.media_url || item.thumbnail_url ? (
          item.media_type === 'video'
            ? <video src={item.media_url} className="w-full h-full object-cover" muted />
            : <img src={item.media_url || item.thumbnail_url} alt={item.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-pink-900/20 to-rose-900/10 flex items-center justify-center">
            <ImageIcon className="w-10 h-10 text-pink-400/40" />
          </div>
        )}
        {item.external_url && (
          <a href={item.external_url} target="_blank" rel="noreferrer" className="absolute top-2 right-2 bg-background/70 p-1.5 rounded-full text-white hover:bg-background/90" onClick={e => e.stopPropagation()}>
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
      </div>
      <div className="p-3">
        <h4 className="font-medium text-sm truncate">{item.title}</h4>
        {item.category && <span className="text-xs text-muted-foreground">{item.category}</span>}
        {item.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {item.tags.slice(0, 3).map((t, i) => <span key={i} className="text-[10px] text-muted-foreground bg-muted/60 px-1.5 py-0.5 rounded">{t}</span>)}
          </div>
        )}
      </div>
    </div>
  );
}

function ServiceItem({ item }) {
  return (
    <Card className="p-4 bg-muted/20 border-border hover:border-amber-500/20 transition-all" data-testid={`service-${item.service_id}`}>
      <div className="flex justify-between items-start mb-2">
        <h4 className="font-semibold text-sm">{item.title}</h4>
        {item.price && (
          <span className="text-sm font-bold text-amber-400 flex items-center gap-0.5">
            <DollarSign className="w-3.5 h-3.5" />{item.price}
            {item.price_type === 'hourly' && <span className="text-xs text-muted-foreground font-normal">/hr</span>}
          </span>
        )}
      </div>
      {item.description && <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{item.description}</p>}
      {item.category && <Badge variant="outline" className="text-[10px] border-border text-muted-foreground mb-2">{item.category}</Badge>}
      {item.includes?.length > 0 && (
        <ul className="text-xs text-muted-foreground space-y-0.5 mb-2">
          {item.includes.slice(0, 3).map((inc, i) => <li key={i} className="flex items-center gap-1"><Star className="w-3 h-3 text-amber-500/50" />{inc}</li>)}
        </ul>
      )}
      {item.duration && <p className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" />{item.duration}</p>}
    </Card>
  );
}

// ── Main profile page ───────────────────────────────────────────────

export default function CreativeProfilePage() {
  const { handle } = useParams();
  const navigate = useNavigate();
  const { user, token, isAuthenticated } = useAuth();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [showBooking, setShowBooking] = useState(false);
  const [contactMsg, setContactMsg] = useState('');
  const [bookingMsg, setBookingMsg] = useState('');
  const [bookingBudget, setBookingBudget] = useState('');
  const [sending, setSending] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => { fetchProfile(); }, [handle]);

  const fetchProfile = async () => {
    try {
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const res = await fetch(`${API}/creatives/${handle}`, { headers });
      if (!res.ok) { navigate('/discover'); return; }
      setProfile(await res.json());

      if (isAuthenticated) {
        const fRes = await fetch(`${API}/users/${handle}/follow-status`, { headers });
        if (fRes.ok) { const d = await fRes.json(); setIsFollowing(d.is_following); }
      }
    } catch {} finally { setLoading(false); }
  };

  const toggleFollow = async () => {
    if (!isAuthenticated) { navigate('/login'); return; }
    try {
      const ep = isFollowing ? `${API}/users/${profile.user_id}/unfollow` : `${API}/users/${profile.user_id}/follow`;
      const res = await fetch(ep, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        setIsFollowing(!isFollowing);
        setProfile(p => ({ ...p, followers_count: (p.followers_count || 0) + (isFollowing ? -1 : 1) }));
      }
    } catch { toast.error('Failed'); }
  };

  const sendMessage = async () => {
    if (!contactMsg.trim()) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/messages/conversations`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipient_user_id: profile.user_id, initial_message: contactMsg })
      });
      if (res.ok) { toast.success('Message sent!'); setShowContact(false); setContactMsg(''); }
      else toast.error('Failed to send');
    } catch { toast.error('Error'); } finally { setSending(false); }
  };

  const sendBooking = async () => {
    if (!bookingMsg.trim()) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/creatives/${profile.user_id}/book`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: bookingMsg, budget: bookingBudget, type: profile.actions?.includes('hire') ? 'hire' : 'book' })
      });
      if (res.ok) { toast.success('Booking request sent!'); setShowBooking(false); setBookingMsg(''); }
      else toast.error('Failed');
    } catch { toast.error('Error'); } finally { setSending(false); }
  };

  const playItem = (item) => {
    const url = item.audio_url || item.external_url;
    if (!url) { if (item.external_url) window.open(item.external_url, '_blank'); return; }
    const id = item.beat_id || item.episode_id || item.track_id;
    if (playingId === id) { audioRef.current?.pause(); setPlayingId(null); return; }
    if (audioRef.current) audioRef.current.pause();
    const a = new Audio(url);
    a.play().catch(() => {});
    a.onended = () => setPlayingId(null);
    audioRef.current = a;
    setPlayingId(id);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-orange-400" /></div>;
  if (!profile) return <div className="min-h-screen flex items-center justify-center"><p className="text-muted-foreground">Not found</p></div>;

  const p = profile;
  const role = p.role || 'listener';
  const colors = ROLE_COLORS[role] || ROLE_COLORS.artist;
  const Icon = ROLE_ICONS[role] || Music;
  const actions = p.actions || [];
  const contentTitle = CONTENT_TITLES[p.content_type] || 'Content';

  return (
    <div className="min-h-screen" data-testid="creative-profile-page">
      <audio ref={audioRef} className="hidden" />

      {/* Banner */}
      <div className={`h-44 md:h-56 relative bg-gradient-to-br ${colors.gradient} via-zinc-900`}>
        {p.banner_url && <img src={p.banner_url} className="w-full h-full object-cover absolute inset-0" alt="" />}
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 to-transparent" />
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="absolute top-4 left-4 bg-black/40 backdrop-blur-sm text-white hover:bg-background/60">
          <ChevronLeft className="w-5 h-5" />
        </Button>
      </div>

      <div className="max-w-5xl mx-auto px-4 -mt-14 relative z-10 pb-16">
        {/* Header */}
        <div className="flex flex-col md:flex-row gap-5 items-start">
          <div className="flex-shrink-0">
            {p.avatar_url ? (
              <img src={p.avatar_url} alt={p.name} className="w-28 h-28 md:w-32 md:h-32 rounded-2xl object-cover border-4 border-border shadow-xl" />
            ) : (
              <div className={`w-28 h-28 md:w-32 md:h-32 rounded-2xl ${colors.accent} flex items-center justify-center border-4 border-border shadow-xl`}>
                <Icon className="w-14 h-14 text-foreground/80" />
              </div>
            )}
          </div>

          <div className="flex-1 pt-4 md:pt-6">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground" data-testid="creative-name">{p.name}</h1>
              <Badge className={`${colors.badge} font-semibold`} data-testid="role-badge"><Icon className="w-3.5 h-3.5 mr-1" />{p.role_label}</Badge>
              {p.is_verified && <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">Verified</Badge>}
            </div>
            <p className="text-muted-foreground text-sm mb-2">{p.handle ? `@${p.handle}` : ""}</p>

            {p.location && <p className="text-muted-foreground text-sm flex items-center gap-1 mb-2"><MapPin className="w-3.5 h-3.5" />{p.location}</p>}
            {(p.studio_name || p.show_name) && <p className="text-foreground/80 text-sm mb-2 font-medium">{p.studio_name || p.show_name}</p>}
            {p.bio && <p className="text-foreground/80 text-sm mb-3 max-w-xl">{p.bio}</p>}

            {p.genres?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {p.genres.map((g, i) => <Badge key={i} variant="outline" className="text-xs border-border text-foreground/80">{g}</Badge>)}
              </div>
            )}
            {p.specialties?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {p.specialties.map((s, i) => <span key={i} className="text-xs text-muted-foreground flex items-center gap-0.5"><Tag className="w-3 h-3" />{s}</span>)}
              </div>
            )}
            {p.rate_range && <p className="text-sm text-muted-foreground mb-2 flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" />{p.rate_range}</p>}
            {p.schedule && <p className="text-sm text-muted-foreground mb-2 flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{p.schedule}</p>}
            {p.equipment?.length > 0 && <p className="text-xs text-muted-foreground mb-2">Equipment: {p.equipment.join(', ')}</p>}
            {p.amenities?.length > 0 && <p className="text-xs text-muted-foreground mb-2">Amenities: {p.amenities.join(', ')}</p>}
            {p.daw && <p className="text-xs text-muted-foreground mb-2">DAW: {p.daw}</p>}

            <div className="flex items-center gap-5 text-sm text-muted-foreground mb-3">
              <span><strong className="text-foreground">{p.followers_count || 0}</strong> followers</span>
              <span><strong className="text-foreground">{p.content_count || 0}</strong> {CONTENT_TITLES[p.content_type]?.toLowerCase() || 'items'}</span>
            </div>

            {p.work_with_me && <Badge className="bg-green-500/20 text-green-400 border-green-500/30 mb-3"><Briefcase className="w-3.5 h-3.5 mr-1" />Open to Work</Badge>}

            {/* Action buttons */}
            <div className="flex gap-2 flex-wrap">
              {actions.includes('follow') && (
                <Button data-testid="follow-btn" onClick={toggleFollow} variant={isFollowing ? 'outline' : 'default'}
                  className={!isFollowing ? `${colors.accent} hover:opacity-90 text-white` : 'border-border'} size="sm">
                  {isFollowing ? <><UserMinus className="w-4 h-4 mr-1.5" />Unfollow</> : <><UserPlus className="w-4 h-4 mr-1.5" />Follow</>}
                </Button>
              )}
              {actions.includes('contact') && (
                <Button data-testid="contact-btn" variant="outline" size="sm" className="border-border" onClick={() => setShowContact(true)}>
                  <MessageSquare className="w-4 h-4 mr-1.5" />Contact
                </Button>
              )}
              {(actions.includes('hire') || actions.includes('book')) && (
                <Button data-testid="book-btn" variant="outline" size="sm" className="border-border" onClick={() => setShowBooking(true)}>
                  <Briefcase className="w-4 h-4 mr-1.5" />{actions.includes('hire') ? 'Hire' : 'Book'}
                </Button>
              )}
              <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground"><Share2 className="w-4 h-4" /></Button>
            </div>
          </div>
        </div>

        {/* Contact Modal */}
        {showContact && (
          <div className="fixed inset-0 bg-background/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowContact(false)}>
            <Card className="w-full max-w-md bg-card border-border p-5" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-lg">Contact {p.name}</h3>
                <Button variant="ghost" size="icon" onClick={() => setShowContact(false)}><X className="w-4 h-4" /></Button>
              </div>
              <Textarea data-testid="contact-input" placeholder={`Hey ${p.name}, I'd love to work with you...`}
                value={contactMsg} onChange={e => setContactMsg(e.target.value)} rows={4} className="bg-muted/50 border-border mb-3" />
              <Button className={`w-full ${colors.accent} text-white`} onClick={sendMessage} disabled={sending || !contactMsg.trim()}>
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-2" />}Send
              </Button>
            </Card>
          </div>
        )}

        {/* Booking Modal */}
        {showBooking && (
          <div className="fixed inset-0 bg-background/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowBooking(false)}>
            <Card className="w-full max-w-md bg-card border-border p-5" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-lg">{actions.includes('hire') ? 'Hire' : 'Book'} {p.name}</h3>
                <Button variant="ghost" size="icon" onClick={() => setShowBooking(false)}><X className="w-4 h-4" /></Button>
              </div>
              <Textarea data-testid="booking-message" placeholder="Describe what you need..."
                value={bookingMsg} onChange={e => setBookingMsg(e.target.value)} rows={4} className="bg-muted/50 border-border mb-3" />
              <Input data-testid="booking-budget" placeholder="Budget (optional)" value={bookingBudget}
                onChange={e => setBookingBudget(e.target.value)} className="bg-muted/50 border-border mb-3" />
              <Button className={`w-full ${colors.accent} text-white`} onClick={sendBooking} disabled={sending || !bookingMsg.trim()}>
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Briefcase className="w-4 h-4 mr-2" />}Send Request
              </Button>
            </Card>
          </div>
        )}

        {/* Content Section */}
        {p.content_type && (
          <div className="mt-10">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold text-foreground flex items-center gap-2" data-testid="content-heading">
                <Icon className="w-5 h-5" style={{ opacity: 0.7 }} />{contentTitle}
              </h2>
              <span className="text-sm text-muted-foreground">{p.content?.length || 0} {contentTitle.toLowerCase()}</span>
            </div>

            {(!p.content || p.content.length === 0) ? (
              <div className="text-center py-14 text-muted-foreground">
                <Icon className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p>No {contentTitle.toLowerCase()} yet</p>
              </div>
            ) : p.content_type === 'beats' ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {p.content.map(item => <BeatItem key={item.beat_id} item={item} onPlay={playItem} isPlaying={playingId === item.beat_id} />)}
              </div>
            ) : p.content_type === 'episodes' ? (
              <div className="space-y-3">
                {p.content.map(item => <EpisodeItem key={item.episode_id} item={item} onPlay={playItem} isPlaying={playingId === item.episode_id} />)}
              </div>
            ) : p.content_type === 'portfolio' ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {p.content.map(item => <PortfolioItem key={item.item_id} item={item} />)}
              </div>
            ) : p.content_type === 'services' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {p.content.map(item => <ServiceItem key={item.service_id} item={item} />)}
              </div>
            ) : p.content_type === 'tracks' ? (
              <div className="space-y-2">
                {p.content.map(item => (
                  <div key={item.track_id} className="flex items-center gap-3 p-3 rounded-xl bg-muted/20 border border-border hover:border-violet-500/20 transition-all cursor-pointer" onClick={() => playItem(item)}>
                    <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                      {item.cover_art_url ? <img src={item.cover_art_url} alt="" className="w-full h-full object-cover" /> :
                        <div className="w-full h-full bg-violet-900/30 flex items-center justify-center"><Music className="w-5 h-5 text-violet-400/50" /></div>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium truncate">{item.title}</h4>
                      <p className="text-xs text-muted-foreground">{item.genre}</p>
                    </div>
                    <div className="w-8 h-8 rounded-full bg-violet-600 flex items-center justify-center flex-shrink-0">
                      {playingId === item.track_id ? <Pause className="w-3.5 h-3.5 text-white" /> : <Play className="w-3.5 h-3.5 text-white ml-0.5" />}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        )}

        {/* Credits section (Engineers) */}
        {p.credits?.length > 0 && (
          <div className="mt-8">
            <h3 className="font-bold text-foreground mb-3 flex items-center gap-2"><Star className="w-4 h-4 text-amber-400" />Credits</h3>
            <div className="flex flex-wrap gap-2">
              {p.credits.map((c, i) => <Badge key={i} variant="outline" className="text-xs border-border text-foreground/80">{c}</Badge>)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
